// Harmonized status palette — the single source of truth for the multi-hue
// status indicators used across the dashboards (appointment / application
// statuses). Re-toned to sit within the Vaidvana bone/forest/saffron
// aesthetic while staying distinguishable from one another.
//
// Each tone exposes:
//   main — the dot / chip / border / icon color
//   bg   — a soft tint for chip/badge backgrounds
//   fg   — a readable foreground for text on `bg`
//
// This is a plain constant (not theme-dependent) so the many module-level
// STATUS_CONFIG maps can import it directly without hooks. It is also exposed
// on the theme as `theme.brand.status` for use inside sx callbacks.
import type { AppointmentStatus } from '../types/common';
import type { DoctorApplicationStatus } from '../types/doctorApplication';

export interface StatusTone {
  main: string;
  bg: string;
  fg: string;
}

export type StatusToneName =
  | 'success'
  | 'info'
  | 'pending'
  | 'warning'
  | 'error'
  | 'neutral';

export const statusColors: Record<StatusToneName, StatusTone> = {
  success: { main: '#2E6F4E', bg: '#E7F0EA', fg: '#1B4A33' }, // forest green
  info: { main: '#3F7E78', bg: '#E7F0EF', fg: '#244B47' }, // deep sage-teal
  pending: { main: '#C2902F', bg: '#FBF1DD', fg: '#7A5A18' }, // saffron
  warning: { main: '#C77B30', bg: '#FBEEDF', fg: '#7C4A18' }, // ochre
  error: { main: '#C45A3F', bg: '#FAE7E1', fg: '#7E3322' }, // terracotta
  neutral: { main: '#6B736D', bg: '#EFEDE8', fg: '#3A403C' }, // stone
};

export const APPOINTMENT_STATUS_TONE: Record<AppointmentStatus, StatusToneName> = {
  PENDING_PAYMENT: 'pending',
  CONFIRMED: 'info',
  RESCHEDULED: 'warning',
  COMPLETED: 'success',
  CANCELLED: 'error',
  ABSENT: 'neutral',
  EXPIRED: 'neutral',
};

export const APPLICATION_STATUS_TONE: Record<DoctorApplicationStatus, StatusToneName> = {
  PENDING: 'pending',
  UNDER_REVIEW: 'info',
  NEEDS_MORE_INFORMATION: 'warning',
  APPROVED: 'success',
  REJECTED: 'error',
  WITHDRAWN: 'neutral',
};

/** Flat map of appointment status -> `main` color (for chips/dots that only
 *  need a single color). */
export const appointmentStatusColor = Object.fromEntries(
  (Object.keys(APPOINTMENT_STATUS_TONE) as AppointmentStatus[]).map(s => [
    s,
    statusColors[APPOINTMENT_STATUS_TONE[s]].main,
  ])
) as Record<AppointmentStatus, string>;

/** Convenience: resolve a tone object for an appointment status. */
export const appointmentStatusTone = (status: AppointmentStatus): StatusTone =>
  statusColors[APPOINTMENT_STATUS_TONE[status] ?? 'neutral'];
